import React, { useState } from 'react';
import {
  Loader,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  PiggyBank,
  Receipt,
  Star,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RootState } from '../../../reducers';
import { planPricesPerMonth } from '../../../constants/prices';
import { usePlan } from '../../plan/hooks/plan';
import { createOrUpdateSubscription } from '../../plan/thunk';
import { pushModal } from '../../globalModals/globalModalsSlice';
import { setAlertMessage } from '../dashboardSlice';

// NOTE: This modal doesn't support weekly subscription terms - only monthly and annual
// TODO: Add weekly support when needed
const UpSellModal = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const source = useSelector(
    (state: RootState) => state.globalModals.actionData.source,
  );

  const {
    term,
    price: currentPrice,
    provider,
    state: subscriptionState,
  } = useSelector((state: RootState) => state.plan.subscription);

  // Check if user is on a free trial
  const isTrialing = subscriptionState === 'trialing';
  const currentPricePerMonth =
    term === 'monthly' ? currentPrice : currentPrice / 12;
  const {
    team,
    settings,
    plan: planState,
  } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId || team.teams[0].teamId;
  const { teamPlan, isPlan } = usePlan();

  // Check if the current user is the payer
  const currentUserId = settings.user._id;
  const payerUserId =
    planState.payer?.userId || planState.subscription?.payerUserId;
  const ownerId = team.selectedTeam?.ownerId;
  const currentPayerId = payerUserId || ownerId;
  const isCurrentPayer = currentUserId === currentPayerId;

  const [isLoading, setIsLoading] = useState(false);
  const [showAllBenefits, setShowAllBenefits] = useState(false);

  // Don't show upsell modal to enterprise users
  if (isPlan(['ENTERPRISE'])) {
    onClose();
    return null;
  }

  // Check if user is subscribed through mobile payment provider
  const isMobileProvider = provider === 'apple' || provider === 'android';

  const dialogType =
    // eslint-disable-next-line no-nested-ternary
    source === 'limits'
      ? 'unlimited'
      : term === 'monthly'
        ? 'annual'
        : 'unlimited';

  // Minimal unlimited content - focused on core value
  const unlimitedPrice = planPricesPerMonth.BUSINESS[term] ?? 0;

  const dialogContent = [
    {
      type: 'annual',
      title: t('drawer.upsell.annual.title'),
      content: (
        <>
          <p className="mb-6 text-center text-muted-foreground">
            {t('drawer.upsell.annual.subtitle')}
          </p>
          <div className="mb-6 grid gap-6">
            <Card className="border-none bg-muted/50">
              <CardContent className="p-5">
                <div className="flex flex-col gap-5">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <PiggyBank className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="mb-1 font-medium">
                        {t('drawer.upsell.annual.features.discount.title')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'drawer.upsell.annual.features.discount.description',
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <Receipt className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="mb-1 font-medium">
                        {t('drawer.upsell.annual.features.billing.title')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('drawer.upsell.annual.features.billing.description')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <Star className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="mb-1 font-medium">
                        {t('drawer.upsell.annual.features.features.title')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'drawer.upsell.annual.features.features.description',
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      ),
      newPrice:
        teamPlan in planPricesPerMonth
          ? planPricesPerMonth[teamPlan as keyof typeof planPricesPerMonth]
              .annual
          : 0,
    },
    {
      type: 'unlimited',
      title: t('drawer.upsell.unlimited.headline'),
      content: (
        <div className="space-y-5">
          {/* Core benefits - minimal */}
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xl font-semibold text-primary">∞</span>
                <span className="font-medium">
                  {t('drawer.upsell.unlimited.benefits.transcription')}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xl font-semibold text-primary">∞</span>
                <span className="font-medium">
                  {t('drawer.upsell.unlimited.benefits.ai')}
                </span>
              </div>
            </div>
          </div>

          {/* See all benefits - expandable, between the two boxes */}
          <div>
            <button
              type="button"
              onClick={() => setShowAllBenefits(!showAllBenefits)}
              className="flex w-full items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('drawer.upsell.unlimited.seeAllBenefits')}
              {showAllBenefits ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>

            <div
              className={`grid transition-all duration-200 ease-out ${
                showAllBenefits
                  ? 'grid-rows-[1fr] opacity-100 mt-3'
                  : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <div className="rounded-lg bg-muted/50 p-4">
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>
                      • {t('drawer.upsell.unlimited.allBenefits.integrations')}
                    </li>
                    <li>
                      • {t('drawer.upsell.unlimited.allBenefits.vocabulary')}
                    </li>
                    <li>
                      • {t('drawer.upsell.unlimited.allBenefits.bulkUpload')}
                    </li>
                    <li>
                      • {t('drawer.upsell.unlimited.allBenefits.whiteLabel')}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing comparison - compact */}
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="flex items-center justify-center gap-6">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">
                  {t('drawer.upsell.unlimited.currentPlanLabel')}
                </p>
                <p className="text-lg font-semibold">
                  {isTrialing
                    ? t('drawer.upsell.pricing.freeTrial')
                    : `$${currentPricePerMonth}`}
                </p>
                {!isTrialing && (
                  <p className="text-xs text-muted-foreground">
                    {t('drawer.upsell.pricing.month')}
                  </p>
                )}
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <div className="text-center">
                <p className="text-xs text-muted-foreground">
                  {t('drawer.upsell.unlimited.unlimitedLabel')}
                </p>
                <p className="text-lg font-semibold text-primary">
                  ${unlimitedPrice}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('drawer.upsell.pricing.month')}
                </p>
              </div>
            </div>
          </div>

          {term === 'annual' && (
            <p className="text-center text-xs text-muted-foreground">
              {isTrialing
                ? t('drawer.upsell.pricing.billedAnnuallyNoProrate', {
                    price: unlimitedPrice * 12,
                  })
                : t('drawer.upsell.pricing.billedAnnually', {
                    price: unlimitedPrice * 12,
                  })}
            </p>
          )}

          {isTrialing && (
            <p className="text-center text-xs text-muted-foreground">
              {t('drawer.upsell.pricing.trialActivationNotice')}
            </p>
          )}
        </div>
      ),
      newPrice: unlimitedPrice,
    },
  ];

  const currentDialog = dialogContent.find(
    (dialog) => dialog.type === dialogType,
  );

  const handleUpgrade = async () => {
    // If the current user is not the payer, redirect to the full upgrade modal
    // which includes the payment form to collect their card details
    if (!isCurrentPayer) {
      onClose();
      dispatch(
        pushModal({
          type: 'stripe-pricing-table-upgrade-modal',
        }),
      );
      return;
    }

    setIsLoading(true);
    try {
      const newPlan = dialogType === 'annual' ? teamPlan : 'BUSINESS';
      const newTerm = dialogType === 'annual' ? 'annual' : term;
      const newPrice =
        (currentDialog?.newPrice ?? 0) * (dialogType === 'annual' ? 12 : 1);

      const result = await dispatch(
        createOrUpdateSubscription({
          teamId,
          plan: newPlan,
          term: newTerm,
          totalAmount: newPrice,
        }),
      );
      if (createOrUpdateSubscription.rejected.match(result as never)) {
        onClose();
        const { message = '' } = (result as any).error || {};
        throw new Error(message);
      }

      onClose();
      dispatch(
        pushModal({
          type: 'upgradeSuccess',
        }),
      );
    } catch (error) {
      console.error('Upgrade failed:', error);
      dispatch(
        setAlertMessage({
          type: 'error',
          message: t('drawer.upsell.pricing.error.message', {
            error: error.message,
          }),
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const calculateSavings = () => {
    if (currentDialog && 'newPrice' in currentDialog) {
      if (dialogType === 'annual') {
        const monthlySavings =
          (currentPricePerMonth ?? 0) - (currentDialog.newPrice ?? 0);
        const yearlySavings = monthlySavings * 12;
        return (
          <div className="my-6 flex flex-col gap-4">
            <Card className="border-none bg-muted/50">
              <CardContent className="p-5">
                <div className="text-center">
                  <p className="mb-1 text-sm font-medium text-green-600 dark:text-green-500">
                    {t('drawer.upsell.pricing.yearlySavings')}
                  </p>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-500">
                    ${yearlySavings}
                  </p>
                </div>

                <div className="mt-5">
                  <div className="mb-2 flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t('drawer.upsell.pricing.currentMonthly')}
                    </span>
                    <span className="text-sm font-medium">
                      {t('drawer.upsell.pricing.perMonth', {
                        price: currentPricePerMonth,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t('drawer.upsell.pricing.newMonthly')}
                    </span>
                    <span className="text-sm font-medium">
                      {t('drawer.upsell.pricing.perMonth', {
                        price: currentDialog.newPrice,
                      })}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <p className="text-center text-xs text-muted-foreground">
              {t('drawer.upsell.pricing.billedAnnually', {
                price: (currentDialog.newPrice ?? 0) * 12,
              })}
            </p>
          </div>
        );
      }
      if (dialogType === 'unlimited') {
        // Pricing is now handled in the content itself for the minimal design
        return null;
      }
    }
    return null;
  };

  const getButtonText = () => {
    if (dialogType === 'annual') {
      return t('drawer.upsell.buttons.switchAnnual');
    }
    if (dialogType === 'unlimited') {
      if (isTrialing) {
        return t('drawer.upsell.buttons.activateNow');
      }
      return t('drawer.upsell.buttons.upgradeNow');
    }
    return '';
  };

  const getDialogTitle = () => {
    if (isMobileProvider) {
      return t('drawer.upsell.mobileProvider.title');
    }
    if (currentDialog && 'newPrice' in currentDialog) {
      if (dialogType === 'annual') {
        return t('drawer.upsell.annual.title');
      }
      return currentDialog.title;
    }
    return '';
  };

  const renderMobileProviderMessage = () => {
    const platform = provider === 'apple' ? 'iOS' : 'Android';

    return (
      <div className="mt-6">
        <Card className="border-none bg-muted/50">
          <CardContent className="p-5">
            <div className="text-center">
              <p className="mb-3 text-sm text-muted-foreground">
                {t('drawer.upsell.mobileProvider.message', { platform })}
              </p>
              <p className="text-sm font-medium">
                {t('drawer.upsell.mobileProvider.instruction')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[420px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
        </DialogHeader>
        <div className="py-4 overflow-y-auto">
          {currentDialog?.content}
          {isMobileProvider
            ? renderMobileProviderMessage()
            : calculateSavings()}
        </div>
        <DialogFooter>
          {isMobileProvider ? (
            <Button onClick={onClose} className="w-full">
              {t('drawer.upsell.buttons.close')}
            </Button>
          ) : (
            <Button
              onClick={handleUpgrade}
              disabled={isLoading}
              className="w-full gap-2"
              size="lg"
            >
              {isLoading && <Loader className="h-4 w-4 animate-spin" />}
              {getButtonText()}
              {!isLoading && <ArrowRight className="h-4 w-4" />}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpSellModal;
